import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { formatCredit, type CreditHistory, type CreditResult } from '../../services/credit';
import { subscribeCreditRefresh } from '../../services/creditRefresh';

/** 本人收支与账户日志：分页读取原始记录，不将钱包余额反推成历史发放。 */
export default function CreditHistoryPanel({ scale, locale }: { scale: number; locale: string }) {
  const [filter, setFilter] = useState({ type: 0, page: 1 });
  const [result, setResult] = useState<CreditResult<CreditHistory> | null>(null);
  const [loading, setLoading] = useState(true);
  const sequence = useRef(0);
  const t = (key: string) => i18nService.t(`credit${key}`);
  /** 刷新立即清除旧页；请求顺序和认证传输层共同隔离迟到响应。 */
  const refresh = useCallback(async () => {
    const current = ++sequence.current;
    setLoading(true); setResult(null);
    const next = await authService.credits.history(filter.type, filter.page);
    if (current !== sequence.current) return;
    setResult(next); setLoading(false);
  }, [filter]);
  useEffect(() => {
    void refresh();
    const unsubscribe = subscribeCreditRefresh(() => void refresh());
    return () => { sequence.current++; unsubscribe(); };
  }, [refresh]);
  const data = result?.status === 'ready' ? result.data : null;
  const button = 'rounded-lg border border-claude-border dark:border-claude-darkBorder px-3 py-1.5 text-xs disabled:opacity-50';
  return <section className="space-y-3 py-3" aria-label={t('History')}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <select aria-label={t('HistoryFilter')} value={filter.type} onChange={event => setFilter({ type: Number(event.target.value), page: 1 })}
        className="rounded-lg border border-claude-border dark:border-claude-darkBorder bg-claude-bg dark:bg-claude-darkBg p-2 text-sm">
        {[0, 1, 2, 3, 4, 5, 6].map(type => <option key={type} value={type}>{t(`LogType${type}`)}</option>)}
      </select>
      <button type="button" className={button} disabled={loading} onClick={() => void refresh()}>{t('Refresh')}</button>
    </div>
    <p className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('HistoryHint')}</p>
    {loading ? <p role="status" className="py-8 text-sm">{t('Loading')}</p> : !data ?
      <p role="status" className="py-8 text-sm">{t(result?.status === 'unsupported' ? 'Unsupported' : result?.status === 'forbidden' ? 'Forbidden' : result?.status === 'unauthenticated' ? 'SignIn' : 'Unavailable')}</p> : <>
        {data.items.length === 0 && <p className="py-8 text-sm">{t('Empty')}</p>}
        {data.items.map((row, index) => <article key={`${row.id}:${index}`} className="space-y-1 border-b border-claude-border dark:border-claude-darkBorder pb-3 text-sm">
          <div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{t(`LogType${row.type <= 6 ? row.type : 0}`)}</span>
            <time className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{new Date(row.created_at * 1000).toLocaleString(locale)}</time></div>
          <p className="break-words whitespace-pre-wrap">{row.content || row.model_name || '—'}</p>
          {row.type === 2 && <p className="tabular-nums">{t('HistoryUsed')}: {formatCredit(row.credit_mcr, scale, locale)} {t('Unit')}
            {row.credit_mcr_shadow != null && row.credit_mcr_shadow > 0 && <span className="ml-2">{t('HistorySimulated')}: {formatCredit(row.credit_mcr_shadow, scale, locale)} {t('Unit')}</span>}</p>}
        </article>)}
        <div className="flex items-center justify-between gap-2">
          <button type="button" className={button} disabled={filter.page === 1} onClick={() => setFilter(current => ({ ...current, page: current.page - 1 }))}>{t('HistoryPrevious')}</button>
          <span className="text-xs tabular-nums">{filter.page} / {Math.max(1, Math.ceil(data.total / 20))}</span>
          <button type="button" className={button} disabled={filter.page * 20 >= data.total} onClick={() => setFilter(current => ({ ...current, page: current.page + 1 }))}>{t('HistoryNext')}</button>
        </div>
      </>}
  </section>;
}
